/**
 * azure-functions/shared/state/evidenceRequestMigration.cjs
 *
 * Canonical (GH #7115 pattern): shared by the browser, which re-exports it from src/, and the Function App, so the server can run the exact load path the browser runs (measurement-parity-diff). Edit this file, never the src/ re-export.
 *
 * Idempotent migration of the withdrawn evidence-request structure onto the one
 * task system (ADR-0003).
 *
 * Called from migrateState() in scfMigration.js on every state load.
 *
 * Before (PR #2628 and earlier — a parallel structure):
 *   { id, name, …, ownerId, status: 'not_started'|'in_progress'|'ready'|'na' }
 *
 * After (Tier 2 — domain record + domain-backed action):
 *   {
 *     id, name, description, category, controlIds, catalogueId, derivationKey,
 *     attachedDocIds, attachedArtefactIds, linkedAuditId,   ← evidence: domain data
 *     notApplicable, naReason,                               ← applicability, not a status
 *     actions: [ DomainAction ],                             ← the work item
 *     legacy: { status, ownerId, migratedAt },                ← frozen archive
 *   }
 *
 * The record keeps the evidence, per the redesign doc's "evidence belongs to
 * domains, not tasks". The actionable item moves to `actions[]` using the same
 * DomainAction shape `risk.actions[]` already uses — so the old four-value enum
 * is retired rather than replaced by a fifth one.
 *
 * Nothing is destroyed: the original `status` and `ownerId` are archived under
 * `legacy` so the pre-July-2026 record of what each tenant actually did survives
 * even if a status mapping later turns out to be wrong for someone.
 */

'use strict';

// src/utils/formatting.js's generateId / now, inlined: that module is ESM
// and browser-only. Node 24 and every supported browser expose crypto.
const generateId = () => crypto.randomUUID();
const now = () => new Date().toISOString();

// ── Old status enum → DomainAction status ───────────────────────────────────
//
// 'na' is deliberately absent. "Not applicable" is a statement about the
// evidence item, not a state of work someone is progressing, so it becomes an
// applicability flag on the domain record and produces no action at all —
// otherwise every N/A row would migrate into an open task nobody should ever do.
const STATUS_MAP = {
  not_started: 'pending',
  in_progress: 'in-progress',
  ready: 'completed',
};

/**
 * Migrate legacy evidence requests onto Tier 2 domain-backed actions.
 *
 * Idempotent by two independent guards:
 *   1. a record that already has an `actions` array is left completely alone;
 *   2. `legacy.migratedAt` records that the archive step already ran.
 *
 * @param {object} state - Tenant state (mutated in place)
 * @returns {object} state
 */
function migrateEvidenceRequests(state) {
  const requests = state.evidenceRequests;
  if (!Array.isArray(requests) || requests.length === 0) return state;

  for (const req of requests) {
    // Already on the new shape — never re-derive, or completing an action would
    // be undone on the next load.
    if (Array.isArray(req.actions)) continue;

    const legacyStatus = req.status || 'not_started';
    const legacyOwnerId = req.ownerId || null;

    req.notApplicable = legacyStatus === 'na';
    req.naReason = req.naReason || '';

    const actionStatus = STATUS_MAP[legacyStatus];
    req.actions = actionStatus
      ? [
          {
            id: generateId(),
            title: req.name ? `Produce evidence: ${req.name}` : 'Produce evidence',
            description: req.description || '',
            status: actionStatus,
            assigneeId: legacyOwnerId || '',
            dueDate: null,
            createdAt: req.createdAt || now(),
            completedAt:
              actionStatus === 'completed' ? req.updatedAt || req.createdAt || now() : null,
            completedBy: actionStatus === 'completed' ? 'migration' : null,
            evidenceRefs: [],
          },
        ]
      : [];

    req.legacy = { status: legacyStatus, ownerId: legacyOwnerId, migratedAt: now() };
    delete req.status;
    delete req.ownerId;
  }

  return state;
}

module.exports = {
  migrateEvidenceRequests,
};
